<template>
  <pwa-layout title="Nouveau Bon" subtitle="GESTION DE FLOTTE" :show-back="true">
    <div class="pwa-form-page">

      <!-- ── Section : Bon ── -->
      <div class="pwa-section-title">Informations du bon</div>
      <div class="pwa-form-card">

        <!-- Véhicule -->
        <div class="pwa-field">
          <label class="pwa-field-label">VÉHICULE <span class="pwa-required">*</span></label>
          <div class="pwa-select-wrap">
            <span class="mdi mdi-car-outline pwa-input-icon"></span>
            <select v-model="form.vehicle" class="pwa-select" @change="onVehicleChange">
              <option value="" disabled>Sélectionner un véhicule</option>
              <option v-for="v in vehicles" :key="v.id" :value="v.id">
                {{ v.registration_number }}{{ driverSuffix(v) }}
              </option>
            </select>
            <span class="mdi mdi-chevron-down pwa-select-arrow"></span>
          </div>
        </div>

        <!-- Entreprise (isBusiness) -->
        <div v-if="isBusiness" class="pwa-field">
          <label class="pwa-field-label">ENTREPRISE <span class="pwa-required">*</span></label>
          <div class="pwa-select-wrap">
            <span class="mdi mdi-domain pwa-input-icon"></span>
            <select v-model="form.recipient_company" class="pwa-select" :disabled="lockCompany" @change="onCompanyChange">
              <option value="" disabled>Sélectionner l'entreprise</option>
              <option v-for="c in companies" :key="c.pk" :value="c.pk">{{ c.name }}</option>
            </select>
            <span class="mdi mdi-chevron-down pwa-select-arrow"></span>
          </div>
        </div>

        <!-- Bon en montant uniquement -->
        <div class="pwa-toggle-row">
          <div>
            <div class="pwa-toggle-label">Bon en montant uniquement</div>
            <div class="pwa-toggle-sub">Pas de produit ni de litrage</div>
          </div>
          <label class="pwa-switch">
            <input type="checkbox" v-model="form.is_all_money" />
            <span class="pwa-slider"></span>
          </label>
        </div>

        <!-- Produit -->
        <div v-if="!form.is_all_money" class="pwa-field">
          <label class="pwa-field-label">PRODUIT <span class="pwa-required">*</span></label>
          <div class="pwa-select-wrap">
            <span class="mdi mdi-gas-station pwa-input-icon"></span>
            <select v-model="form.company_product" class="pwa-select" :disabled="!products.length || vehicleAsProduct" @change="onProductChange">
              <option value="" disabled>Sélectionner le produit</option>
              <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <span class="mdi mdi-chevron-down pwa-select-arrow"></span>
          </div>
        </div>

        <!-- Site -->
        <div v-if="shops.length" class="pwa-field">
          <label class="pwa-field-label">SITE</label>
          <div class="pwa-select-wrap">
            <span class="mdi mdi-store-outline pwa-input-icon"></span>
            <select v-model="form.company_shop" class="pwa-select">
              <option value="" disabled>Sélectionner le site</option>
              <option v-for="s in shops" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <span class="mdi mdi-chevron-down pwa-select-arrow"></span>
          </div>
        </div>

        <!-- Litres -->
        <div v-if="!form.is_all_money" class="pwa-field">
          <label class="pwa-field-label">NOMBRE DE LITRES <span class="pwa-required">*</span></label>
          <div class="pwa-input-wrap">
            <span class="mdi mdi-water-outline pwa-input-icon"></span>
            <input v-model.number="form.purchase_liters" class="pwa-input" type="number" placeholder="Ex: 50" @input="onLitersInput" />
            <span class="pwa-input-suffix">L</span>
          </div>
        </div>

        <!-- Montant -->
        <div class="pwa-field">
          <label class="pwa-field-label">MONTANT TOTAL</label>
          <div class="pwa-input-wrap">
            <span class="mdi mdi-cash-outline pwa-input-icon"></span>
            <input v-model.number="form.purchase_amount" class="pwa-input" type="number" placeholder="Ex: 25000" @input="onAmountInput" />
            <span class="pwa-input-suffix">F CFA</span>
          </div>
        </div>

        <!-- Fréquence -->
        <div class="pwa-field">
          <label class="pwa-field-label">FRÉQUENCE <span class="pwa-required">*</span></label>
          <div class="pwa-select-wrap">
            <span class="mdi mdi-refresh pwa-input-icon"></span>
            <select v-model="form.frequency" class="pwa-select">
              <option value="" disabled>Sélectionner la fréquence</option>
              <option v-for="f in frequencyItems" :key="f.value" :value="f.value">{{ f.text }}</option>
            </select>
            <span class="mdi mdi-chevron-down pwa-select-arrow"></span>
          </div>
        </div>

        <!-- Date d'expiration -->
        <div class="pwa-field">
          <label class="pwa-field-label">DATE D'EXPIRATION <span class="pwa-required">*</span></label>
          <div class="pwa-datetime-row">
            <div class="pwa-input-wrap pwa-datetime-date">
              <span class="mdi mdi-calendar-outline pwa-input-icon"></span>
              <input v-model="validDate" class="pwa-input" type="date" />
            </div>
            <div class="pwa-input-wrap pwa-datetime-time">
              <span class="mdi mdi-clock-outline pwa-input-icon"></span>
              <input v-model="validTime" class="pwa-input" type="time" />
            </div>
          </div>
        </div>

        <!-- N° BL -->
        <div class="pwa-field">
          <label class="pwa-field-label">N° BL</label>
          <div class="pwa-input-wrap">
            <span class="mdi mdi-barcode pwa-input-icon"></span>
            <input v-model="form.bl_number" class="pwa-input" placeholder="Numéro de bon de livraison" />
          </div>
        </div>

        <!-- Destination -->
        <div class="pwa-field">
          <label class="pwa-field-label">DESTINATION</label>
          <div class="pwa-input-wrap">
            <span class="mdi mdi-map-marker-outline pwa-input-icon"></span>
            <input v-model="form.destination" class="pwa-input" placeholder="Ex: Abidjan - Bouaké" />
          </div>
        </div>

        <!-- Superposition (si Single) -->
        <div v-if="canAllowSuccessive" class="pwa-toggle-row">
          <div>
            <div class="pwa-toggle-label">Autoriser la superposition</div>
            <div class="pwa-toggle-sub">Disponible uniquement pour Non-renouvellable</div>
          </div>
          <label class="pwa-switch">
            <input type="checkbox" v-model="form.allow_successive" />
            <span class="pwa-slider"></span>
          </label>
        </div>

        <!-- Accumulation (si non Single) -->
        <div v-if="!canAllowSuccessive" class="pwa-toggle-row">
          <div>
            <div class="pwa-toggle-label">Accumuler le restant</div>
            <div class="pwa-toggle-sub">Le solde non consommé sera reporté</div>
          </div>
          <label class="pwa-switch">
            <input type="checkbox" v-model="form.accumulate_remaining" />
            <span class="pwa-slider"></span>
          </label>
        </div>
      </div>

      <!-- Résumé -->
      <div class="pwa-summary-card">
        <div class="pwa-summary-title">
          <span class="mdi mdi-file-document-outline" style="margin-right:6px;color:var(--pwa-accent)"></span>
          RÉSUMÉ
        </div>
        <div v-if="!form.is_all_money" class="pwa-summary-row">
          <span class="pwa-summary-label">Litres</span>
          <span class="pwa-summary-value">{{ form.purchase_liters || '—' }} L</span>
        </div>
        <div class="pwa-summary-row">
          <span class="pwa-summary-label">Montant</span>
          <span class="pwa-summary-value">{{ form.purchase_amount ? formatAmount(form.purchase_amount) + ' F' : '—' }}</span>
        </div>
        <div v-if="form.is_all_money" class="pwa-summary-row">
          <span class="pwa-summary-label">Type</span>
          <span class="pwa-summary-value">Montant uniquement</span>
        </div>
      </div>

      <div v-if="error" class="pwa-error">{{ error }}</div>
    </div>

    <!-- Submit -->
    <div class="pwa-submit-area">
      <button class="pwa-submit-btn" :disabled="saving || !canSubmit" @click="generate">
        <span v-if="saving" class="mdi mdi-loading mdi-spin" style="margin-right:8px"></span>
        <span v-else class="mdi mdi-check-circle-outline" style="margin-right:8px"></span>
        Générer le bon
      </button>
    </div>
  </pwa-layout>
</template>

<script>
import PwaLayout from './PwaLayout.vue'
import CompanyVehiclesService from '@/services/CompanyVehiclesService'
import CompanyVouchersService from '@/services/CompanyVouchersService'
import CompanyService from '@/services/CompanyService'
import BusinessService from '@/services/BusinessService'
import { FREQUENCY_ITEMS } from '@/utils/consts'

export default {
  name: 'PwaNewVoucher',
  components: { PwaLayout },
  data() {
    return {
      form: {
        vehicle: '',
        recipient_company: '',
        company_product: '',
        company_shop: '',
        purchase_liters: null,
        purchase_amount: null,
        frequency: '',
        valid_until: '',
        bl_number: '',
        destination: '',
        allow_successive: false,
        accumulate_remaining: false,
        is_all_money: false,
        product_price: 0,
      },
      vehicles: [],
      companies: [],
      products: [],
      shops: [],
      balance: 0,
      validDate: '',
      validTime: '23:59',
      frequencyItems: FREQUENCY_ITEMS,
      vehicleAsProduct: false,
      updatingField: null,
      saving: false,
      error: null,
    }
  },
  computed: {
    isBusiness() {
      return (this.$store.getters['app/getCompany']?.company_type || '').toLowerCase() === 'business'
    },
    isPrincipal() {
      return !!(this.$store.getters['app/getCompany']?.is_principal)
    },
    lockCompany() {
      return this.isPrincipal || (this.companies.length === 1 && !this.isBusiness)
    },
    canAllowSuccessive() {
      const v = typeof this.form.frequency === 'string'
        ? this.form.frequency
        : (this.form.frequency?.value || '')
      return ['single', 'onetime'].includes(String(v).toLowerCase())
    },
    newBalance() {
      return Math.max(0, this.balance - (this.form.purchase_amount || 0))
    },
    validUntilISO() {
      if (!this.validDate) return null
      return `${this.validDate}T${this.validTime || '23:59'}:00Z`
    },
    canSubmit() {
      if (this.form.is_all_money) {
        return this.form.vehicle && this.form.purchase_amount && this.form.frequency && this.validDate
      }
      return this.form.vehicle && this.form.purchase_liters && this.form.frequency && this.validDate
    },
  },
  watch: {
    'form.is_all_money'(val) {
      if (val) {
        this.form.company_product = ''
        this.form.purchase_liters = null
        this.form.product_price = 0
      }
    },
    'form.frequency'(val) {
      if (!this.canAllowSuccessive && this.form.allow_successive) {
        this.form.allow_successive = false
      }
      if (this.canAllowSuccessive && this.form.accumulate_remaining) {
        this.form.accumulate_remaining = false
      }
    },
  },
  mounted() { this.loadData() },
  methods: {
    async loadData() {
      try {
        const [vData, companies, homeData] = await Promise.all([
          CompanyVehiclesService.get(this, null, { all: true }),
          CompanyService.getAll(),
          BusinessService.getHome().catch(() => null),
        ])
        this.vehicles = Array.isArray(vData) ? vData : (vData?.results || [])
        this.companies = companies || []
        this.balance = homeData?.total_credit || homeData?.voucher_kpi || 0

        // Auto-sélection si une seule entreprise ou mode verrouillé
        if (this.lockCompany && this.companies.length > 0) {
          this.form.recipient_company = this.companies[0].pk
          this.updateProductsAndShops(this.companies[0].pk)
        }
      } catch (e) { console.error(e) }
    },
    onVehicleChange() {
      const vehicle = this.vehicles.find(v => v.id === this.form.vehicle)
      this.vehicleAsProduct = false
      if (vehicle?.product) {
        const linked = this.products.find(p => p.id === vehicle.product)
        if (linked) {
          this.vehicleAsProduct = true
          this.form.company_product = linked.id
          this.form.product_price = linked.price || 0
          this.recalcAmount()
        }
      }
    },
    onCompanyChange() {
      this.updateProductsAndShops(this.form.recipient_company)
    },
    updateProductsAndShops(companyId) {
      const company = this.companies.find(c => c.pk === companyId)
      if (!company) return
      this.products = company.products || []
      this.shops = [{ id: '', name: 'Tout le réseau' }, ...(company.shops || [])]
      // Vérif que les sélections sont encore valides
      if (!this.products.find(p => p.id === this.form.company_product)) {
        this.form.company_product = ''
        this.form.product_price = 0
      }
      if (!this.shops.find(s => s.id === this.form.company_shop)) {
        this.form.company_shop = ''
      }
      this.recalcAmount()
    },
    onProductChange() {
      const p = this.products.find(p => p.id === this.form.company_product)
      this.form.product_price = p?.price || 0
      this.recalcAmount()
    },
    onLitersInput() {
      if (this.updatingField === 'amount') return
      this.updatingField = 'liters'
      if (this.form.product_price) {
        this.form.purchase_amount = Number((this.form.purchase_liters * this.form.product_price).toFixed(0))
      }
      this.$nextTick(() => { this.updatingField = null })
    },
    onAmountInput() {
      if (this.updatingField === 'liters') return
      this.updatingField = 'amount'
      if (this.form.product_price) {
        this.form.purchase_liters = Number((this.form.purchase_amount / this.form.product_price).toFixed(2))
      }
      this.$nextTick(() => { this.updatingField = null })
    },
    recalcAmount() {
      if (this.form.product_price && this.form.purchase_liters) {
        this.form.purchase_amount = Number((this.form.purchase_liters * this.form.product_price).toFixed(0))
      }
    },
    driverSuffix(v) {
      const name = `${v.driver_last_name || ''} ${v.driver_first_name || ''}`.trim()
      return name ? ` — ${name}` : ''
    },
    formatAmount(val) {
      return Math.round(Number(val || 0)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
    },
    async generate() {
      if (!this.canSubmit) return
      this.saving = true
      this.error = null
      try {
        const payload = {
          vehicle: this.form.vehicle,
          purchase_liters: this.form.purchase_liters,
          purchase_amount: this.form.purchase_amount,
          frequency: this.form.frequency,
          valid_until: this.validUntilISO || undefined,
          company_product: this.form.company_product || undefined,
          company_shop: this.form.company_shop || undefined,
          recipient_company: this.form.recipient_company || undefined,
          bl_number: this.form.bl_number || undefined,
          destination: this.form.destination || undefined,
          allow_successive: this.form.allow_successive,
          accumulate_remaining: this.form.accumulate_remaining,
        }
        await CompanyVouchersService.create(this, payload)
        this.$router.push('/pwa/vouchers')
      } catch (e) {
        const data = e?.response?.data
        if (data && typeof data === 'object') {
          this.error = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n')
        } else {
          this.error = 'Une erreur est survenue.'
        }
      } finally { this.saving = false }
    },
  },
}
</script>

<style scoped>
.pwa-form-page { padding: 16px 16px 120px; display: flex; flex-direction: column; gap: 0; }

.pwa-datetime-row { display: flex; gap: 8px; }
.pwa-datetime-date { flex: 3; }
.pwa-datetime-time { flex: 2; }

.pwa-section-title {
  font-size: 12px; font-weight: 700; color: var(--pwa-muted);
  letter-spacing: 0.8px; text-transform: uppercase;
  padding: 16px 4px 8px;
}
.pwa-form-card {
  background: var(--pwa-white); border-radius: var(--pwa-card-radius);
  padding: 16px; display: flex; flex-direction: column; gap: 16px;
  margin-bottom: 14px;
}

.pwa-field { display: flex; flex-direction: column; gap: 6px; }
.pwa-field-label {
  font-size: 11px; font-weight: 600; color: var(--pwa-muted);
  letter-spacing: 0.8px; text-transform: uppercase;
}
.pwa-required { color: var(--pwa-error); }

.pwa-input-wrap, .pwa-select-wrap {
  display: flex; align-items: center; gap: 10px;
  background: var(--pwa-bg); border-radius: 12px; padding: 0 14px;
  height: 50px; border: 1.5px solid transparent; transition: border-color 0.2s;
}
.pwa-input-wrap:focus-within, .pwa-select-wrap:focus-within { border-color: var(--pwa-accent); }
.pwa-input-icon { font-size: 18px; color: var(--pwa-muted); flex-shrink: 0; }
.pwa-input {
  flex: 1; border: none; outline: none;
  font-size: 15px; color: var(--pwa-text); background: transparent;
}
.pwa-input::placeholder { color: var(--pwa-muted); }
.pwa-input-suffix { font-size: 13px; font-weight: 500; color: var(--pwa-muted); flex-shrink: 0; }
.pwa-select {
  flex: 1; border: none; outline: none;
  font-size: 15px; color: var(--pwa-text); background: transparent;
  -webkit-appearance: none; appearance: none; cursor: pointer;
}
.pwa-select:disabled { opacity: 0.5; }
.pwa-select-arrow { font-size: 18px; color: var(--pwa-muted); flex-shrink: 0; pointer-events: none; }

/* Toggle */
.pwa-toggle-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding-top: 4px; }
.pwa-toggle-label { font-size: 14px; font-weight: 600; color: var(--pwa-text); }
.pwa-toggle-sub { font-size: 12px; color: var(--pwa-muted); margin-top: 2px; }
.pwa-switch { position: relative; display: inline-block; width: 46px; height: 26px; flex-shrink: 0; }
.pwa-switch input { opacity: 0; width: 0; height: 0; }
.pwa-slider { position: absolute; cursor: pointer; inset: 0; background: #D1D9E6; border-radius: 26px; transition: background 0.2s; }
.pwa-slider:before { content: ''; position: absolute; width: 20px; height: 20px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: transform 0.2s; }
.pwa-switch input:checked + .pwa-slider { background: var(--pwa-accent); }
.pwa-switch input:checked + .pwa-slider:before { transform: translateX(20px); }

/* Summary */
.pwa-summary-card {
  background: var(--pwa-white); border-radius: var(--pwa-card-radius); padding: 16px;
  border: 1.5px solid #E8F0FE; margin-bottom: 14px;
}
.pwa-summary-title { font-size: 11px; font-weight: 600; color: var(--pwa-accent); letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 12px; display: flex; align-items: center; }
.pwa-summary-row { display: flex; justify-content: space-between; align-items: center; padding: 5px 0; }
.pwa-summary-label { font-size: 13px; color: var(--pwa-muted); }
.pwa-summary-value { font-size: 14px; font-weight: 500; color: var(--pwa-text); }
.pwa-summary-label--bold { font-weight: 600; color: var(--pwa-text); }
.pwa-summary-value--bold { font-size: 15px; font-weight: 700; }
.pwa-summary-divider { height: 1px; background: #F0F4F8; margin: 6px 0; }

.pwa-error { color: var(--pwa-error); font-size: 13px; padding: 10px 14px; background: #FFEBEE; border-radius: 10px; white-space: pre-line; margin-bottom: 14px; }

.pwa-submit-area {
  position: fixed; bottom: var(--pwa-nav-h); left: 50%; transform: translateX(-50%);
  width: 100%; max-width: 480px; padding: 12px 16px;
  background: var(--pwa-bg); border-top: 1px solid rgba(0,0,0,0.06); z-index: 10;
}
.pwa-submit-btn {
  width: 100%; height: 52px; border-radius: 16px;
  background: var(--pwa-dark); color: white;
  font-size: 16px; font-weight: 600; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: opacity 0.2s;
}
.pwa-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
